import { createContext, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";

/**
 * The channel a screen uses to name the record it is editing — FR-4.
 *
 * `studioChrome` has always taken a `recordLabel`, and the shell has always
 * passed nothing, so `/studio/assets/:id` read `STUDIO / ASSETS / UNTITLED`
 * for every asset. The trail told you the depth and then lied about where you
 * were.
 *
 * The label cannot be derived in the shell: the shell has the creator record,
 * not the asset, and the screen that fetched the asset is the only thing that
 * knows its title. So it travels the same way the guard's dirty state does —
 * the child declares, the shell renders — which keeps `routeMap` pure and
 * keeps the breadcrumb derived from the route rather than hand-authored per
 * screen (W-1).
 */

type Setter = (label: string | undefined) => void;

const RecordLabelContext = createContext<Setter | null>(null);

export function RecordLabelProvider({
  children,
}: {
  /** Given the label currently declared, if any. */
  children: (label: string | undefined) => ReactNode;
}) {
  const [label, setLabel] = useState<string | undefined>(undefined);
  return (
    <RecordLabelContext.Provider value={setLabel}>
      {children(label)}
    </RecordLabelContext.Provider>
  );
}

/**
 * Declares the title of the record on screen. Pass `undefined` while it is
 * still loading — the breadcrumb then reads UNTITLED, which is honest, rather
 * than holding the *previous* asset's title, which is not.
 *
 * The cleanup exists so the label cannot outlive the screen that set it. It is
 * not observable through the breadcrumb today - `routeMap` reads `recordLabel`
 * on `/studio/assets/:id` alone, and any screen mounted there declares one - so
 * per W-6 it is specified and checked in `RecordLabel.test.tsx` rather than
 * left as a claim the shell's tests appear to cover but do not.
 *
 * Cleanup and setup run in the same commit when the label changes, so React
 * batches them into one render rather than flashing UNTITLED between titles.
 */
export function useRecordLabel(label: string | undefined) {
  const setLabel = useContext(RecordLabelContext);
  useEffect(() => {
    if (!setLabel) return;
    setLabel(label);
    return () => setLabel(undefined);
  }, [setLabel, label]);
}
